import React, { Component } from "react";
import ReactDOM from "react-dom";
import { withTranslation } from "react-i18next";
import { post } from "../../../utils/sysAxios";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";
import TicketDetailModal from "./TicketDetailModal";

// ── Palette ───────────────────────────────────────────────────────────────────
const COLORS = ["#2563EB", "#16A34A", "#DC2626", "#D97706", "#7C3AED", "#0891B2"];

const TICKET_STATUSES = [
  { name: "Submitted",           color: "#6C757D", icon: "fa fa-inbox",        bg: "#6C757D15" },
  { name: "Assigned to Team",    color: "#FFB200", icon: "fa fa-users",         bg: "#FFB20015" },
  { name: "Employee in the way", color: "#FFB200", icon: "fa fa-street-view",   bg: "#FFB20015" },
  { name: "Processing",          color: "#F57F17", icon: "fa fa-cog",           bg: "#F57F1715" },
  { name: "Completed",           color: "#28A745", icon: "fa fa-check-circle",  bg: "#28A74515" },
];

const STATUS_COLORS = {
  "Submitted":           "#6C757D",
  "Assigned to Team":    "#FFB200",
  "Employee in the way": "#FFB200",
  "Processing":          "#F57F17",
  "Completed":           "#28A745",
};

function getStatusColor(name) {
  return STATUS_COLORS[name] || "#6C757D";
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const styles = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');

.td-root {
  font-family: 'IBM Plex Sans', sans-serif;
  background: #F8FAFC;
  min-height: 100vh;
  padding: 0 0 40px 0;
  color: #1E293B;
  margin: -12px -12px -12px -2px;
  width: calc(100% + 14px);
  box-sizing: border-box;
  overflow-x: hidden;
}
[dir="rtl"] .td-root,
[dir="rtl"] .td-root *:not(i):not([class*="fa"]):not([class*="pi"]) {
  font-family: 'Cairo', 'IBM Plex Sans', sans-serif !important;
}

/* RTL table alignment */
[dir="rtl"] .td-table th { text-align: right !important; }
[dir="rtl"] .td-table td { text-align: right !important; }

/* RTL drill-down search */
[dir="rtl"] .td-drilldown-search-wrap i { left: auto !important; right: 11px !important; }
[dir="rtl"] .td-drilldown-search-wrap input { padding: 8px 32px 8px 12px !important; }

/* RTL drill-down panel slides from the left instead */
[dir="rtl"] .td-ticket-item:hover { transform: translateX(-3px); }

/* RTL status card arrow flips to left */
[dir="rtl"] .td-sc-arrow { right: auto !important; left: 12px !important; }

/* ── Top bar ── */
.td-topbar {
  background: #1E293B;
  color: #fff;
  padding: 16px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-bottom: 3px solid #2563EB;
}
.td-title {
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 10px;
}
.td-title span.accent { color: #60A5FA; }
.td-live-dot {
  width: 9px; height: 9px;
  border-radius: 50%;
  background: #22C55E;
  display: inline-block;
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.4; transform: scale(1.3); }
}

/* ── Filter bar ── */
.td-filterbar {
  background: #fff;
  border-bottom: 1px solid #E2E8F0;
  padding: 10px 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
}
.td-period-btn {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
  color: #475569;
  flex-shrink: 0;
  white-space: nowrap;
}
.td-period-btn:hover { background: #EFF6FF; border-color: #2563EB; color: #2563EB; }
.td-period-btn.active { background: #2563EB; border-color: #2563EB; color: #fff; }
.td-sep { width: 1px; height: 24px; background: #E2E8F0; margin: 0 4px; }
.td-filterbar .p-dropdown {
  border-radius: 8px !important;
  font-size: 0.82rem !important;
  width: 150px !important;
  min-width: 120px !important;
  flex-shrink: 0 !important;
}
.td-filterbar .p-calendar {
  width: 190px !important;
  flex-shrink: 0 !important;
}
.td-filterbar .p-calendar input { font-size: 0.82rem !important; width: 100% !important; }

/* ── Main content ── */
.td-body { padding: 24px 28px; }

/* ── KPI Cards ── */
.td-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}
.td-kpi {
  background: #fff;
  border-radius: 12px;
  padding: 20px 22px;
  border: 1px solid #E2E8F0;
  border-left: 4px solid var(--accent, #2563EB);
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
  transition: box-shadow .2s;
}
.td-kpi:hover { box-shadow: 0 4px 14px rgba(0,0,0,.09); }
.td-kpi-icon {
  font-size: 1.35rem;
  opacity: .85;
  margin-bottom: 2px;
}
.td-kpi-val {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  font-family: 'IBM Plex Mono', monospace;
  color: var(--accent, #2563EB);
}
.td-kpi-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.td-kpi-sub {
  font-size: 0.72rem;
  color: #94A3B8;
}

/* ── Section header ── */
.td-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #94A3B8;
  margin: 0 0 14px 0;
}

/* ── Charts row ── */
.td-chart-row {
  display: grid;
  grid-template-columns: 1fr 1.6fr 2fr;
  gap: 16px;
  margin-bottom: 28px;
}
@media (max-width: 1100px) {
  .td-chart-row { grid-template-columns: 1fr 1fr; }
  .td-chart-row .td-chart-card:last-child { grid-column: 1 / -1; }
}
@media (max-width: 700px) {
  .td-chart-row { grid-template-columns: 1fr; }
  .td-chart-row .td-chart-card:last-child { grid-column: auto; }
}
.td-chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.td-chart-card h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 7px;
}

/* ── Tables row ── */
.td-table-row {
  display: grid;
  grid-template-columns: 1fr 1.8fr;
  gap: 16px;
}
@media (max-width: 900px) { .td-table-row { grid-template-columns: 1fr; } }
.td-table-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.td-table-card h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 14px 0;
  display: flex;
  align-items: center;
  gap: 7px;
}
.td-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
}
.td-table th {
  text-align: left;
  padding: 7px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #94A3B8;
  border-bottom: 1px solid #F1F5F9;
}
.td-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #F8FAFC;
  color: #334155;
  vertical-align: middle;
}
.td-table tr:last-child td { border-bottom: none; }
.td-table tr:hover td { background: #F8FAFC; }
.td-sla-row { cursor: pointer; }
.td-sla-row:hover td { background: #EFF6FF !important; }

/* ── Badges ── */
.td-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: .03em;
}
.td-badge-red    { background: #FEF2F2; color: #DC2626; }
.td-badge-yellow { background: #FFFBEB; color: #B45309; }
.td-badge-blue   { background: #EFF6FF; color: #1D4ED8; }
.td-badge-green  { background: #F0FDF4; color: #15803D; }

/* ── Workload bar ── */
.td-workload-bar-bg {
  height: 6px; border-radius: 3px;
  background: #F1F5F9; width: 100%;
}
.td-workload-bar-fill {
  height: 6px; border-radius: 3px;
  background: #2563EB;
  transition: width .4s ease;
}

/* ── Refresh info ── */
.td-refresh-info {
  font-size: 0.72rem;
  color: #94A3B8;
  font-family: 'IBM Plex Mono', monospace;
}

/* ── Loading overlay ── */
.td-loading-bar {
  height: 3px;
  background: linear-gradient(90deg, #2563EB 0%, #60A5FA 50%, #2563EB 100%);
  background-size: 200% 100%;
  animation: slide 1.2s linear infinite;
}
@keyframes slide {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.td-empty {
  text-align: center;
  color: #94A3B8;
  font-size: 0.83rem;
  padding: 24px 0;
}

/* ── Status Cards Row ── */
.td-status-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.td-status-card {
  background: #fff;
  border-radius: 12px;
  padding: 18px 16px;
  border: 1px solid #E2E8F0;
  border-top: 4px solid var(--sc-color);
  cursor: pointer;
  transition: all .18s;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.td-status-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--sc-bg);
  opacity: 0;
  transition: opacity .18s;
}
.td-status-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,.1); }
.td-status-card:hover::before { opacity: 1; }
.td-status-card.active { box-shadow: 0 0 0 2px var(--sc-color), 0 6px 20px rgba(0,0,0,.1); }
.td-status-card.active::before { opacity: 1; }
.td-sc-icon {
  font-size: 1.3rem;
  color: var(--sc-color);
  position: relative;
}
.td-sc-count {
  font-size: 2rem;
  font-weight: 700;
  font-family: 'IBM Plex Mono', monospace;
  color: var(--sc-color);
  line-height: 1;
  position: relative;
}
.td-sc-name {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748B;
  line-height: 1.3;
  position: relative;
}
.td-sc-arrow {
  position: absolute;
  right: 12px; top: 12px;
  font-size: 0.72rem;
  color: var(--sc-color);
  opacity: 0;
  transition: opacity .18s;
}
.td-status-card:hover .td-sc-arrow,
.td-status-card.active .td-sc-arrow { opacity: 1; }

/* ── Drill-down full-screen panel ── */
.td-drilldown-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(15,23,42,.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 99999 !important;
  display: flex;
  align-items: flex-end;
  animation: td-overlay-in .2s ease;
  margin: 0 !important;
  padding: 0 !important;
}
@keyframes td-overlay-in { from { opacity: 0; } to { opacity: 1; } }

.td-drilldown-panel {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 90vh;
  background: #F8FAFC;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -8px 40px rgba(0,0,0,.25);
  animation: td-panel-up .3s cubic-bezier(.33,1,.68,1) forwards;
}
@keyframes td-panel-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

.td-drilldown-header {
  background: #1E293B;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-bottom: 3px solid var(--sc-color, #2563EB);
}
.td-drilldown-header-title {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}
.td-drilldown-count {
  background: var(--sc-color, #2563EB);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  font-family: 'IBM Plex Mono', monospace;
}
.td-drilldown-close {
  width: 34px; height: 34px;
  border-radius: 8px;
  border: 1.5px solid #374151;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem;
  transition: all .15s;
}
.td-drilldown-close:hover { border-color: #EF4444; color: #EF4444; background: rgba(239,68,68,.1); }

.td-drilldown-search {
  background: #fff;
  padding: 10px 24px;
  border-bottom: 1px solid #E2E8F0;
  flex-shrink: 0;
}
.td-drilldown-search-wrap {
  position: relative;
  max-width: 380px;
}
.td-drilldown-search-wrap i {
  position: absolute;
  left: 11px; top: 50%;
  transform: translateY(-50%);
  color: #94A3B8;
  font-size: 0.85rem;
}
.td-drilldown-search-wrap input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1.5px solid #E2E8F0;
  border-radius: 8px;
  font-size: 0.84rem;
  font-family: 'IBM Plex Sans', sans-serif;
  outline: none;
  color: #1E293B;
  transition: border-color .15s;
}
.td-drilldown-search-wrap input:focus { border-color: #2563EB; }

.td-drilldown-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
}
.td-drilldown-body::-webkit-scrollbar { width: 5px; }
.td-drilldown-body::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }

/* Drill-down ticket list */
.td-ticket-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.td-ticket-item {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #E2E8F0;
  padding: 14px 18px;
  cursor: pointer;
  display: grid;
  grid-template-columns: 60px 1fr auto;
  gap: 14px;
  align-items: center;
  transition: all .15s;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
.td-ticket-item:hover {
  border-color: var(--sc-color, #2563EB);
  box-shadow: 0 4px 14px rgba(0,0,0,.09);
  transform: translateX(3px);
}
.td-ticket-item-id {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.75rem;
  color: #94A3B8;
  font-weight: 600;
}
.td-ticket-item-title {
  font-weight: 600;
  font-size: 0.88rem;
  color: #1E293B;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.td-ticket-item-meta {
  font-size: 0.75rem;
  color: #64748B;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.td-ticket-item-meta span { display: flex; align-items: center; gap: 4px; }
.td-ticket-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  flex-shrink: 0;
}
.td-ticket-item-date {
  font-size: 0.7rem;
  font-family: 'IBM Plex Mono', monospace;
  color: #94A3B8;
  white-space: nowrap;
}
.td-ticket-emp {
  font-size: 0.72rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: #F1F5F9;
  color: #475569;
  white-space: nowrap;
}
.td-ticket-emp.unassigned { background: #FEF2F2; color: #DC2626; }
.td-drilldown-empty {
  text-align: center;
  padding: 60px 20px;
  color: #94A3B8;
}
.td-drilldown-empty i { font-size: 2.5rem; opacity: .3; display: block; margin-bottom: 12px; }
.td-drilldown-loading {
  text-align: center;
  padding: 40px;
  color: #94A3B8;
  font-size: 0.85rem;
}

`;

// ── Period options ────────────────────────────────────────────────────────────
const PERIODS = [
  { key: "day",    labelKey: "tick.today" },
  { key: "week",   labelKey: "tick.week" },
  { key: "month",  labelKey: "tick.month" },
  { key: "custom", labelKey: "tick.custom" },
];

// ── Tiny helpers ──────────────────────────────────────────────────────────────
const fmt = (n) => (n === null || n === undefined ? "—" : n.toLocaleString());
const fmtMins = (m) => {
  if (m === null || m === undefined) return "—";
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

class TicketDashboard extends Component {
  state = {
    // Filters
    period: "month",
    customDates: [new Date(new Date().setDate(new Date().getDate() - 29)), new Date()],
    ticketTypeId: null,
    compoundId: null,
    employeeId: null,

    // Filter options
    filterOptions: { ticketTypes: [], compounds: [], employees: [] },

    // Data
    kpis: null,
    charts: null,
    tables: null,

    // UI
    loading: false,
    lastRefresh: null,
    autoRefreshSeconds: 60,
    countdown: 60,

    // Status cards & drill-down
    statusCounts: {},          // { "Submitted": 12, "Processing": 4, ... }
    drillStatus: null,         // currently drilled status name
    drillTickets: [],          // tickets for the drilled status
    drillLoading: false,
    drillSearch: "",

    // Ticket detail modal from drill-down
    detailTicket: null,

    // Ticket detail modal from SLA alerts
    detailTicketSla: null,
  };

  _autoRefreshInterval = null;
  _countdownInterval   = null;

  componentDidMount() {
    this.fetchFilterOptions();
    this.fetchAll();
    this.startAutoRefresh();
  }

  componentWillUnmount() {
    clearInterval(this._autoRefreshInterval);
    clearInterval(this._countdownInterval);
  }

  startAutoRefresh() {
    clearInterval(this._autoRefreshInterval);
    clearInterval(this._countdownInterval);

    this._autoRefreshInterval = setInterval(() => {
      this.fetchAll();
    }, this.state.autoRefreshSeconds * 1000);

    this._countdownInterval = setInterval(() => {
      this.setState((s) => ({ countdown: s.countdown <= 1 ? this.state.autoRefreshSeconds : s.countdown - 1 }));
    }, 1000);

    this.setState({ countdown: this.state.autoRefreshSeconds });
  }

  buildPayload() {
    const { period, customDates, ticketTypeId, compoundId, employeeId } = this.state;
    return {
      period,
      ...(period === "custom" && {
        dateFrom: customDates[0],
        dateTo:   customDates[1],
      }),
      ...(ticketTypeId && { ticketTypeId }),
      ...(compoundId   && { compoundId }),
      ...(employeeId   && { employeeId }),
    };
  }

  fetchFilterOptions() {
    post("/api/data/GetTicketDashboardFilters", {}, this.props.updateIsLoading)
      .then((res) => {
        this.setState({ filterOptions: res.data.respData.data });
      })
      .catch(() => {});
  }

  fetchAll() {
    const payload = this.buildPayload();
    this.setState({ loading: true });

    Promise.all([
      post("/api/data/GetTicketDashboardKPIs",    payload, () => {}),
      post("/api/data/GetTicketDashboardCharts",  payload, () => {}),
      post("/api/data/GetTicketDashboardTables",  payload, () => {}),
    ]).then(([kpiRes, chartRes, tableRes]) => {
      const statusDist = chartRes.data.respData.data?.statusDist || [];
      const statusCounts = {};
      statusDist.forEach(s => { statusCounts[s.label] = s.value; });

      this.setState({
        kpis:        kpiRes.data.respData.data,
        charts:      chartRes.data.respData.data,
        tables:      tableRes.data.respData.data,
        statusCounts,
        loading:     false,
        lastRefresh: new Date(),
        countdown:   this.state.autoRefreshSeconds,
      });
    }).catch(() => {
      this.setState({ loading: false });
    });
  }

  handlePeriodChange(key) {
    this.setState({ period: key }, () => {
      if (key !== "custom") this.fetchAll();
    });
  }

  handleFilterChange(field, val) {
    this.setState({ [field]: val }, () => this.fetchAll());
  }

  handleManualRefresh() {
    this.fetchAll();
    this.startAutoRefresh();
  }

  fetchStatusCounts(payload) {
    // Re-use the charts endpoint — statusDist gives us per-status counts
    post("/api/data/GetTicketDashboardCharts", payload, () => {})
      .then(res => {
        const statusDist = res.data.respData.data?.statusDist || [];
        const statusCounts = {};
        statusDist.forEach(s => { statusCounts[s.label] = s.value; });
        this.setState({ statusCounts });
      }).catch(() => {});
  }

  openDrillDown(statusName) {
    this.setState({ drillStatus: statusName, drillTickets: [], drillLoading: true, drillSearch: "" });
    const payload = { ...this.buildPayload(), statusName };
    post("/api/data/GetTicketsByStatus", payload, () => {})
      .then(res => {
        this.setState({ drillTickets: res.data.respData.data || [], drillLoading: false });
      }).catch(() => this.setState({ drillLoading: false }));
  }

  closeDrillDown() {
    this.setState({ drillStatus: null, drillTickets: [], drillSearch: "" });
  }

  renderStatusCards() {
    const { statusCounts, drillStatus } = this.state;

    return (
      <div className="td-status-cards">
        {TICKET_STATUSES.map((s, i) => {
          const count = statusCounts[s.name] || 0;
          const isActive = drillStatus === s.name;
          return (
            <div
              key={i}
              className={`td-status-card ${isActive ? "active" : ""}`}
              style={{ "--sc-color": s.color, "--sc-bg": s.bg }}
              onClick={() => isActive ? this.closeDrillDown() : this.openDrillDown(s.name)}
              title={`View ${s.name} tickets`}
            >
              <i className={`${s.icon} td-sc-icon`} />
              <div className="td-sc-count">{count}</div>
              <div className="td-sc-name">{s.name}</div>
              <i className={`fa fa-chevron-${isActive ? "down" : "right"} td-sc-arrow`} />
            </div>
          );
        })}
      </div>
    );
  }

  renderDrillDown() {
    const { drillStatus, drillTickets, drillLoading, drillSearch, detailTicket } = this.state;
    if (!drillStatus) return null;

    const statusConf = TICKET_STATUSES.find(s => s.name === drillStatus) || {};
    const color = statusConf.color || "#2563EB";

    const filtered = drillSearch.trim()
      ? drillTickets.filter(t =>
          (t.TITLE       || "").toLowerCase().includes(drillSearch.toLowerCase()) ||
          (t.OWNER_NAME  || "").toLowerCase().includes(drillSearch.toLowerCase()) ||
          (t.UNIT_NAME   || "").toLowerCase().includes(drillSearch.toLowerCase()) ||
          String(t.ID).includes(drillSearch)
        )
      : drillTickets;

    const panel = (
      <>
        <div
          className="td-drilldown-overlay"
          onClick={e => { if (e.target === e.currentTarget) this.closeDrillDown(); }}
        >
          <div className="td-drilldown-panel">
            {/* Panel header */}
            <div className="td-drilldown-header" style={{ "--sc-color": color }}>
              <div className="td-drilldown-header-title">
                <i className={statusConf.icon} style={{ color }} />
                <span style={{ color: "#fff" }}>{drillStatus}</span>
                <span className="td-drilldown-count" style={{ background: color }}>
                  {filtered.length}
                </span>
              </div>
              <button className="td-drilldown-close" onClick={() => this.closeDrillDown()}>
                <i className="fa fa-times" />
              </button>
            </div>

            {/* Search */}
            <div className="td-drilldown-search">
              <div className="td-drilldown-search-wrap">
                <i className="fa fa-search" />
                <input
                  type="text"
                  placeholder={`Search ${drillStatus} tickets…`}
                  value={drillSearch}
                  onChange={e => this.setState({ drillSearch: e.target.value })}
                  autoFocus
                />
              </div>
            </div>

            {/* Ticket list */}
            <div className="td-drilldown-body">
              {drillLoading ? (
                <div className="td-drilldown-loading">
                  <i className="fa fa-circle-o-notch fa-spin" style={{ marginRight: 8 }} />
                  Loading tickets…
                </div>
              ) : filtered.length === 0 ? (
                <div className="td-drilldown-empty">
                  <i className="fa fa-inbox" />
                  <p>{drillSearch ? "No tickets match your search." : `No ${drillStatus} tickets.`}</p>
                </div>
              ) : (
                <div className="td-ticket-list">
                  {filtered.map((t, i) => (
                    <div
                      key={i}
                      className="td-ticket-item"
                      style={{ "--sc-color": color }}
                      onClick={() => this.setState({ detailTicket: t })}
                      title="Click to view ticket details"
                    >
                      <div className="td-ticket-item-id">#{t.ID}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="td-ticket-item-title" title={t.TITLE}>{t.TITLE}</div>
                        <div className="td-ticket-item-meta">
                          <span><i className="fa fa-user" /> {t.OWNER_NAME || "—"}</span>
                          <span><i className="fa fa-home" /> {t.UNIT_NAME || "—"}</span>
                          {t.TYPE_NAME    && <span><i className="fa fa-tag" /> {t.TYPE_NAME}</span>}
                          {t.COMPOUND_NAME && <span><i className="fa fa-building" /> {t.COMPOUND_NAME}</span>}
                        </div>
                      </div>
                      <div className="td-ticket-item-right">
                        <div className="td-ticket-item-date">
                          {t.INSERT_DATE ? new Date(t.INSERT_DATE).toLocaleDateString() : "—"}
                        </div>
                        <div className={`td-ticket-emp ${!t.EMPLOYEE_NAME ? "unassigned" : ""}`}>
                          {t.EMPLOYEE_NAME || "Unassigned"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ticket Detail Modal launched from drill-down — also portaled */}
        {detailTicket && (
          <TicketDetailModal
            ticketId={detailTicket.ID}
            ticketData={detailTicket}
            defaultTab="replies"
            isLoading={false}
            updateIsLoading={() => {}}
            onClose={(refresh) => {
              this.setState({ detailTicket: null });
              if (refresh) this.openDrillDown(drillStatus);
            }}
          />
        )}
      </>
    );

    // Portal to document.body so the overlay is never clipped by the sidebar's stacking context
    return ReactDOM.createPortal(panel, document.body);
  }

  // ── Renderers ──────────────────────────────────────────────────────────────

  renderKPIs() {
    const { t } = this.props;
    const { kpis } = this.state;
    const cards = [
      {
        icon: "fa fa-ticket",
        label: t("tick.openTickets"),
        val: kpis?.totalOpen,
        accent: "#2563EB",
        sub: t("tick.currentlyActive"),
      },
      {
        icon: "fa fa-user-times",
        label: t("tick.unassigned"),
        val: kpis?.unassigned,
        accent: "#D97706",
        sub: t("tick.noEmployeeAssigned"),
      },
      {
        icon: "fa fa-exclamation-triangle",
        label: t("tick.slaBreached"),
        val: kpis?.slaBreached,
        accent: "#DC2626",
        sub: t("tick.pastSlaDeadline"),
      },
      {
        icon: "fa fa-check-circle",
        label: t("tick.completedToday"),
        val: kpis?.completedToday,
        accent: "#16A34A",
        sub: t("tick.resolvedToday"),
      },
      {
        icon: "fa fa-clock-o",
        label: t("tick.avgResolution"),
        val: kpis ? `${fmt(kpis.avgResolutionHours)}h` : null,
        accent: "#7C3AED",
        sub: t("tick.avgHoursToClose"),
      },
    ];

    return (
      <div className="td-kpi-row">
        {cards.map((c, i) => (
          <div
            key={i}
            className="td-kpi"
            style={{ "--accent": c.accent }}
          >
            <i className={c.icon} style={{ color: c.accent, fontSize: "1.2rem" }} />
            <div className="td-kpi-val">{c.val !== null && c.val !== undefined ? c.val : "—"}</div>
            <div className="td-kpi-label">{c.label}</div>
            <div className="td-kpi-sub">{c.sub}</div>
          </div>
        ))}
      </div>
    );
  }

  renderCharts() {
    const { charts } = this.state;
    if (!charts) return null;

    const { statusDist, typeDist, dailyTrend } = charts;

    return (
      <div className="td-chart-row">
        {/* Pie – by status */}
        <div className="td-chart-card">
          <h3><i className="fa fa-pie-chart" style={{ color: "#2563EB" }} /> By Status</h3>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie
                data={statusDist}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
              >
                {statusDist.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={getStatusColor(entry.label)}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar – by type */}
        <div className="td-chart-card">
          <h3><i className="fa fa-bar-chart" style={{ color: "#7C3AED" }} /> By Ticket Type</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={typeDist} margin={{ top: 4, right: 10, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" name="Tickets" radius={[4, 4, 0, 0]}>
                {typeDist.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Line – received vs completed */}
        <div className="td-chart-card">
          <h3><i className="fa fa-line-chart" style={{ color: "#16A34A" }} /> Received vs Completed (30d)</h3>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={dailyTrend} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => d.slice(5)} // MM-DD
                interval={4}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                labelFormatter={(l) => `Date: ${l}`}
                formatter={(v, n) => [v, n === "received" ? "Received" : "Completed"]}
              />
              <Legend iconType="circle" iconSize={8} />
              <Line
                type="monotone"
                dataKey="received"
                stroke="#2563EB"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="completed"
                stroke="#16A34A"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  renderTables() {
    const { tables } = this.state;
    if (!tables) return null;

    const { topWorkload, slaAlerts } = tables;
    const maxOpen = topWorkload.length
      ? Math.max(...topWorkload.map((e) => e.openTickets))
      : 1;

    return (
      <div className="td-table-row">
        {/* Top Workload */}
        <div className="td-table-card">
          <h3>
            <i className="fa fa-users" style={{ color: "#2563EB" }} />
            Top Workload
          </h3>
          {topWorkload.length === 0 ? (
            <div className="td-empty">No data</div>
          ) : (
            <table className="td-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Open</th>
                  <th style={{ width: "80px" }}></th>
                </tr>
              </thead>
              <tbody>
                {topWorkload.map((e, i) => (
                  <tr key={i}>
                    <td style={{ color: "#94A3B8", fontFamily: "IBM Plex Mono, monospace", fontSize: "0.75rem" }}>
                      {i + 1}
                    </td>
                    <td style={{ fontWeight: 500 }}>{e.employeeName}</td>
                    <td>
                      <span style={{ fontFamily: "IBM Plex Mono, monospace", fontWeight: 600, color: "#2563EB" }}>
                        {e.openTickets}
                      </span>
                    </td>
                    <td>
                      <div className="td-workload-bar-bg">
                        <div
                          className="td-workload-bar-fill"
                          style={{ width: `${Math.round((e.openTickets / maxOpen) * 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* SLA Alerts */}
        <div className="td-table-card">
          <h3>
            <i className="fa fa-exclamation-circle" style={{ color: "#DC2626" }} />
            SLA Alerts
            <span
              className="td-badge td-badge-red"
              style={{ marginLeft: "auto" }}
            >
              {slaAlerts.filter((x) => x.isBreached).length} breached
            </span>
          </h3>
          {slaAlerts.length === 0 ? (
            <div className="td-empty">No SLA alerts — all tickets within SLA 🎉</div>
          ) : (
            <table className="td-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Employee</th>
                  <th>Elapsed</th>
                  <th>SLA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {slaAlerts.map((t, i) => {
                  const pct = Math.min(100, Math.round((t.elapsedMinutes / t.slaMinutes) * 100));
                  return (
                    <tr
                      key={i}
                      className="td-sla-row"
                      onClick={() => this.setState({ detailTicket: {
                        ID:            t.ID,
                        TITLE:         t.TITLE,
                        STATUS_EN:     t.statusName,
                        TYPE_NAME:     t.typeName,
                        EMPLOYEE_NAME: t.employeeName !== "Unassigned" ? t.employeeName : null,
                        INSERT_DATE:   t.insertDate,
                        SLA_MINUTES:   t.slaMinutes,
                        ELAPSED_MINUTES: t.elapsedMinutes,
                      }})}
                      title={`Click to view ticket #${t.ID}`}
                    >
                      <td style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "0.75rem", color: "#64748B" }}>
                        #{t.ID}
                      </td>
                      <td style={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
                        {t.TITLE}
                      </td>
                      <td>{t.typeName}</td>
                      <td style={{ color: t.employeeName === "Unassigned" ? "#DC2626" : "#334155" }}>
                        {t.employeeName}
                      </td>
                      <td style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "0.75rem" }}>
                        {fmtMins(t.elapsedMinutes)}
                      </td>
                      <td style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "0.75rem", color: "#64748B" }}>
                        {fmtMins(t.slaMinutes)}
                      </td>
                      <td>
                        <span className={`td-badge ${t.isBreached ? "td-badge-red" : "td-badge-yellow"}`}>
                          {t.isBreached ? "BREACHED" : `${pct}%`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const {
      period, customDates, ticketTypeId, compoundId, employeeId,
      filterOptions, loading, lastRefresh, countdown,
    } = this.state;

    const toDropdownOptions = (arr) =>
      [{ label: t("filter.all"), value: null }, ...arr.map((x) => ({ label: x.label, value: x.value }))];

    return (
      <div className="td-root fade-in">
        <style>{styles}</style>

        {/* Loading bar */}
        {loading && <div className="td-loading-bar" />}

        {/* Top bar */}
        <div className="td-topbar">
          <div className="td-title">
            <i className="fa fa-tachometer" />
            {t("pages.ticketDashboard")}
            <span className="td-live-dot" title={t("tick.liveData")} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="td-refresh-info">
              {lastRefresh
                ? `${t("tick.updated")} ${lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${t("tick.nextIn")} ${countdown}s`
                : t("tick.loading")}
            </span>
            <Button
              icon="fa fa-refresh"
              label={t("btn.refresh")}
              className="p-button-rounded p-button-sm"
              style={{ background: "#2563EB", border: "none", fontSize: "0.82rem", padding: "6px 14px" }}
              disabled={loading}
              onClick={() => this.handleManualRefresh()}
            />
          </div>
        </div>

        {/* Filter bar */}
        <div className="td-filterbar">
          {/* Period presets */}
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`td-period-btn ${period === p.key ? "active" : ""}`}
              onClick={() => this.handlePeriodChange(p.key)}
            >
              {t(p.labelKey)}
            </button>
          ))}

          {/* Custom date picker */}
          {period === "custom" && (
            <Calendar
              selectionMode="range"
              showIcon
              value={customDates}
              onChange={(e) => {
                this.setState({ customDates: e.value }, () => {
                  if (e.value && e.value[0] && e.value[1]) this.fetchAll();
                });
              }}
              style={{ width: 190, flexShrink: 0 }}
              placeholder={t("filter.dateRange")}
            />
          )}

          <div className="td-sep" />

          {/* Ticket Type */}
          <Dropdown
            value={ticketTypeId}
            options={toDropdownOptions(filterOptions.ticketTypes)}
            onChange={(e) => this.handleFilterChange("ticketTypeId", e.value)}
            placeholder={t("filter.allTypes")}
          />

          {/* Compound */}
          <Dropdown
            value={compoundId}
            options={toDropdownOptions(filterOptions.compounds)}
            onChange={(e) => this.handleFilterChange("compoundId", e.value)}
            placeholder={t("filter.allCompounds")}
          />

          {/* Employee */}
          <Dropdown
            value={employeeId}
            options={toDropdownOptions(filterOptions.employees)}
            onChange={(e) => this.handleFilterChange("employeeId", e.value)}
            placeholder={t("filter.allEmployees")}
          />
        </div>

        {/* Body */}
        <div className="td-body">
          <p className="td-section-title">{t("tick.ticketsByStatus")}</p>
          {this.renderStatusCards()}

          <p className="td-section-title">{t("tick.keyMetrics")}</p>
          {this.renderKPIs()}

          <p className="td-section-title">{t("tick.analytics")}</p>
          {this.renderCharts()}

          <p className="td-section-title">{t("tick.quickTables")}</p>
          {this.renderTables()}
        </div>

        {/* Drill-down panel + ticket detail modal */}
        {this.renderDrillDown()}

        {/* Standalone ticket detail modal — for SLA alert clicks outside drill-down */}
        {this.state.detailTicket && !this.state.drillStatus && ReactDOM.createPortal(
          <TicketDetailModal
            ticketId={this.state.detailTicket.ID}
            ticketData={this.state.detailTicket}
            defaultTab="replies"
            isLoading={false}
            updateIsLoading={() => {}}
            onClose={() => this.setState({ detailTicket: null })}
          />,
          document.body
        )}
      </div>
    );
  }
}

export default withTranslation()(TicketDashboard);
